import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearToken, getToken, setToken, setUnauthorizedHandler } from "@/lib/api";

const Ctx = createContext(null);
const BRANCH_KEY = "floristos_branch";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branch, setBranchState] = useState(null);

  const loadBranches = useCallback(async (u) => {
    const list = await api.get("/branches");
    setBranches(list);
    const savedId = localStorage.getItem(BRANCH_KEY);
    const pick =
      list.find((b) => b.id === savedId) ||
      list.find((b) => b.id === u?.branch_id) ||
      list[0] ||
      null;
    setBranchState(pick);
    return pick;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setBranches([]);
    setBranchState(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    (async () => {
      try {
        if (getToken()) {
          const me = await api.get("/auth/me");
          setUser(me);
          await loadBranches(me);
        }
      } catch {
        clearToken();
      } finally {
        setReady(true);
      }
    })();
  }, [loadBranches, logout]);

  const login = useCallback(
    async (email, password) => {
      const res = await api.post("/auth/login-json", { email, password, remember_me: true });
      setToken(res.access_token);
      setUser(res.user);
      await loadBranches(res.user);
    },
    [loadBranches]
  );

  const setBranch = useCallback((b) => {
    setBranchState(b);
    if (b) localStorage.setItem(BRANCH_KEY, b.id);
  }, []);

  const can = useCallback(
    (module, action = "view") => !!user?.permissions?.[module]?.includes(action),
    [user]
  );
  const canAny = useCallback((...pairs) => pairs.some(([m, a]) => can(m, a)), [can]);

  const value = useMemo(
    () => ({
      user,
      ready,
      branches,
      branch,
      setBranch,
      login,
      logout,
      refreshBranches: () => loadBranches(user),
      can,
      canAny,
      isOwner: user?.role_name === "OWNER",
      isManagement: user?.role_name === "OWNER" || can("orders", "approve"),
      canSell: can("pos", "create"),
    }),
    [user, ready, branches, branch, setBranch, login, logout, loadBranches, can, canAny]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
